import { PortalConfig } from '../types/GrooverTypes'

/**
 * Anything carrying a `portalConfig` resolves branding — a full `Client` at runtime, or a bare
 * `{ portalConfig }` from the Admin preview, which renders unsaved draft values through this same
 * resolver so the preview cannot drift from what the portal actually does.
 */
export type PortalBrandingSource = { portalConfig?: PortalConfig }

export type PortalBranding = {
  /** Wordmark shown when there is no logo. */
  name: string
  /** Ready-to-render logo url including the cache-buster, or undefined when no logo is configured. */
  logoUrl: string | undefined
  /** Hex color driving the portal accent. */
  primaryColor: string
  /**
   * Accent as bare `r, g, b` components, so a stylesheet can pick any alpha it needs with
   * `rgba(var(--portal-primary-rgb), 0.15)`. The portal uses seven different alphas; enumerating a
   * variable per alpha would be worse than exposing the channels once.
   */
  primaryColorRgb: string
  /** One step darker — button hover and pressed states. */
  primaryColorDark: string
  /** One step lighter — secondary accents, selected borders. */
  primaryColorLight: string
  /** Near-white wash of the accent, for tinted panel backgrounds. */
  primaryColorSurface: string
  /** One step stronger than the surface — progress tracks, hover on a tinted background. */
  primaryColorSurfaceStrong: string
}

export const DEFAULT_PORTAL_NAME = 'Portal'
export const DEFAULT_PORTAL_PRIMARY_COLOR = '#7c3aed'
/**
 * The unbranded portal's existing shades, kept verbatim rather than derived, so a portal with no
 * custom color renders byte-identically to what shipped before branding existed.
 */
const DEFAULT_PORTAL_PRIMARY_DARK = '#6d28d9'
const DEFAULT_PORTAL_PRIMARY_LIGHT = '#8b5cf6'
const DEFAULT_PORTAL_PRIMARY_SURFACE = '#f5f3ff'
const DEFAULT_PORTAL_PRIMARY_SURFACE_STRONG = '#ede9fe'

const HEX_COLOR_REGEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i

/** Expands #abc to #aabbcc and lowercases; returns null for anything that isn't a hex color. */
const normalizeHexColor = (color: string | undefined): string | null => {
  if (!color) return null

  const trimmed = color.trim().toLowerCase()
  if (!HEX_COLOR_REGEX.test(trimmed)) return null

  const hex = trimmed.slice(1)
  if (hex.length === 3) {
    return `#${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`
  }
  return `#${hex}`
}

type Rgb = { r: number; g: number; b: number }

const toRgb = (hex: string): Rgb => {
  const normalized = normalizeHexColor(hex) ?? DEFAULT_PORTAL_PRIMARY_COLOR
  const value = parseInt(normalized.slice(1), 16)
  return { r: (value >> 16) & 0xff, g: (value >> 8) & 0xff, b: value & 0xff }
}

const toHex = ({ r, g, b }: Rgb): string =>
  `#${[r, g, b].map((channel) => Math.round(channel).toString(16).padStart(2, '0')).join('')}`

/** `ratio` 0 keeps the color, 1 becomes the target — plain linear mixing, no perceptual model. */
const mix = (color: Rgb, target: Rgb, ratio: number): Rgb => ({
  r: color.r + (target.r - color.r) * ratio,
  g: color.g + (target.g - color.g) * ratio,
  b: color.b + (target.b - color.b) * ratio
})

const BLACK: Rgb = { r: 0, g: 0, b: 0 }
const WHITE: Rgb = { r: 255, g: 255, b: 255 }

/**
 * Tints are precomputed here rather than with CSS color-mix() so the portal doesn't depend on
 * browser support for it, and so the values are unit-testable.
 */
export const hexToRgba = (hex: string, alpha: number): string => {
  const { r, g, b } = toRgb(hex)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

const buildLogoUrl = (source: PortalBrandingSource): string | undefined => {
  const logo = source.portalConfig?.logo
  if (!logo?.url) return undefined

  // The storage path is stable across uploads, so without this the browser and CDN keep serving the old file.
  return logo.updatedAt ? `${logo.url}?v=${encodeURIComponent(logo.updatedAt)}` : logo.url
}

/**
 * Resolves the branding for a portal. Falls back to Coursely defaults whenever no client is in
 * context (unified portal at app.coursely.se), the client has no portal config, or the config is
 * disabled. Individual fields fall back independently, so a client can set only a color.
 */
export const resolvePortalBranding = (source: PortalBrandingSource | undefined): PortalBranding => {
  const config = source?.portalConfig
  const isEnabled = !!source && config?.enabled === true

  const customColor = isEnabled ? normalizeHexColor(config?.primaryColor) : null
  const primaryColor = customColor ?? DEFAULT_PORTAL_PRIMARY_COLOR
  const name = (isEnabled && config?.name?.trim()) || DEFAULT_PORTAL_NAME

  const rgb = toRgb(primaryColor)

  return {
    name,
    logoUrl: isEnabled && source ? buildLogoUrl(source) : undefined,
    primaryColor,
    primaryColorRgb: `${rgb.r}, ${rgb.g}, ${rgb.b}`,
    // Derived only for a custom color; the default palette keeps its exact original shades.
    primaryColorDark: customColor ? toHex(mix(rgb, BLACK, 0.15)) : DEFAULT_PORTAL_PRIMARY_DARK,
    primaryColorLight: customColor ? toHex(mix(rgb, WHITE, 0.13)) : DEFAULT_PORTAL_PRIMARY_LIGHT,
    primaryColorSurface: customColor ? toHex(mix(rgb, WHITE, 0.94)) : DEFAULT_PORTAL_PRIMARY_SURFACE,
    primaryColorSurfaceStrong: customColor ? toHex(mix(rgb, WHITE, 0.86)) : DEFAULT_PORTAL_PRIMARY_SURFACE_STRONG
  }
}
